/**
 * Phase 81.59 — Conditional-logic visibility evaluator.
 *
 * Single source of truth used by:
 *   - InteractiveDocumentViewer (signing UI rendering)
 *   - PackagePublicView (Finish gate / required counter)
 *   - PackagePublicLinkView (Finish gate / required counter)
 *
 * Mirrors the same logic that lives inside InteractiveDocumentViewer so a
 * Package's parent components can compute the dynamic hidden-field set
 * WITHOUT waiting for the accordion to mount the viewer.
 *
 * Supports two builder formats:
 *   Format A — source-side rules (what MultiPageVisualBuilder saves):
 *     sourceField.conditionalRules = [
 *       { triggerValue, triggerCondition?, action: 'show'|'hide',
 *         targetFieldId, targetTemplateId? }
 *     ]
 *   Format B — target-side rules (legacy/external):
 *     targetField.conditionalLogic = {
 *       operator?: 'AND'|'OR', action, rules: [{ sourceFieldId, condition, value }]
 *     }
 *
 * Cross-document targeting (Packages only)
 * ----------------------------------------
 * A Format A rule may carry `targetTemplateId` pointing at ANOTHER document in
 * the same package. Rules without it (every rule authored before this feature,
 * and every rule in a standalone template) target the field's own document, so
 * nothing about the existing behaviour changes. Use
 * `computePackageHiddenFieldIds` to evaluate a whole package at once;
 * `computeHiddenFieldIds` remains the single-document entry point.
 */

const getRadioGroupName = (f) => {
  return f?.groupName || f?.group_name || f?.id;
};

const fieldTypeOf = (f) => (f?.type || f?.field_type || '').toLowerCase();

/**
 * Read a source field's current value. Radio fields (new model) store the
 * selection under the group key rather than the placement id.
 */
function readSourceValue(fields, values, sourceFieldId) {
  const sourceField = fields.find((f) => f?.id === sourceFieldId);
  if (!sourceField) return values[sourceFieldId];
  const type = fieldTypeOf(sourceField);
  if (type === 'radio') {
    // New model reads via group key. Legacy: read via field id.
    const isNewModel = sourceField.groupName || sourceField.group_name || sourceField.optionValue || sourceField.option_value;
    if (isNewModel) {
      const group = getRadioGroupName(sourceField);
      return values[group] ?? values[sourceFieldId] ?? '';
    }
  }
  return values[sourceFieldId];
}

/** Evaluate one source-side (Format A) rule against its source document's values. */
function evaluateFormatA(sourceField, rule, srcValue) {
  const srcType = fieldTypeOf(sourceField);
  const { triggerValue, triggerCondition } = rule;

  if (srcType === 'checkbox') {
    const isChecked = srcValue === true || srcValue === 'true';
    return triggerValue === true || triggerValue === 'true' ? isChecked : !isChecked;
  }
  if (srcType === 'radio') {
    const isNewModel = sourceField.groupName || sourceField.optionValue;
    if (isNewModel) {
      return String(srcValue || '') === String(triggerValue || sourceField.optionValue || '');
    }
    return String(srcValue || '') === String(triggerValue || '');
  }
  if (['signature', 'initials'].includes(srcType)) {
    const filled = Boolean(srcValue);
    return triggerValue === 'filled' ? filled : !filled;
  }
  if (srcType === 'text') {
    const strVal = String(srcValue || '');
    switch (triggerCondition) {
      case 'filled': return strVal !== '';
      case 'empty': return strVal === '';
      case 'equals': return strVal === String(triggerValue || '');
      case 'contains': return strVal.includes(String(triggerValue || ''));
      default: return strVal !== '';
    }
  }
  if (srcType === 'date') {
    const strVal = String(srcValue || '');
    if (triggerCondition === 'empty') return strVal === '';
    return strVal !== '';
  }
  return String(srcValue || '') === String(triggerValue || '');
}

/** Evaluate one target-side (Format B) rule. */
function evaluateFormatB(rule, sourceValue) {
  const targetValue = rule.value;
  const strSource = sourceValue === undefined || sourceValue === null ? '' : String(sourceValue);
  switch (rule.condition) {
    case 'equals': return strSource === String(targetValue ?? '');
    case 'not_equals': return strSource !== String(targetValue ?? '');
    case 'contains': return strSource.includes(String(targetValue ?? ''));
    case 'not_empty': return strSource !== '' && sourceValue !== false;
    case 'is_empty': return strSource === '' || sourceValue === false || sourceValue === undefined;
    case 'is_checked': return sourceValue === true || sourceValue === 'true';
    case 'is_unchecked': return !(sourceValue === true || sourceValue === 'true');
    default: return false;
  }
}

/**
 * Resolve one target field's collected rule entries into a hide/show verdict.
 * Shared by the single-document and package evaluators so both agree exactly.
 */
function shouldHideTarget(entries) {
  const formatA = entries.filter((e) => e.format === 'A');
  const formatB = entries.filter((e) => e.format === 'B');

  let shouldHide = false;
  let shouldShow = null;

  formatA.forEach((entry) => {
    if (!entry.matched) return;
    if (entry.rule.action === 'hide') shouldHide = true;
    if (entry.rule.action === 'show') shouldShow = true;
  });

  const hasShowRule = formatA.some((e) => e.rule.action === 'show');
  if (hasShowRule && shouldShow !== true) shouldHide = true;

  if (formatB.length > 0) {
    const operator = (formatB[0].operator || 'AND').toUpperCase();
    const action = formatB[0].defaultAction || 'show';
    const results = formatB.map((e) => e.matched);
    const allMatch = operator === 'OR' ? results.some(Boolean) : results.every(Boolean);
    if (action === 'show' && !allMatch) shouldHide = true;
    if (action === 'hide' && allMatch) shouldHide = true;
  }

  return shouldHide;
}

/**
 * Collect this document's rule entries, keyed by target field id.
 *
 * @param {Array}  fields       - placements for the source document
 * @param {Object} values       - values for the source document
 * @param {Object} [opts]
 * @param {string} [opts.templateId]   - the source document's template id
 * @param {Function} [opts.emit]       - (targetTemplateId, targetFieldId, entry) => void
 */
function collectEntries(fields, values, { templateId = null, emit } = {}) {
  fields.forEach((sourceField) => {
    const rules = sourceField.conditionalRules || [];
    rules.forEach((rule) => {
      if (!rule?.targetFieldId) return;
      const srcValue = readSourceValue(fields, values, sourceField.id);
      emit(rule.targetTemplateId || templateId, rule.targetFieldId, {
        format: 'A',
        rule,
        matched: evaluateFormatA(sourceField, rule, srcValue),
      });
    });
  });

  // Format B is target-side and always same-document: the rule lives on the
  // field it controls and names a source within the same placement list.
  fields.forEach((targetField) => {
    const cl = targetField.conditionalLogic;
    if (!cl || !Array.isArray(cl.rules) || cl.rules.length === 0) return;
    cl.rules.forEach((rule) => {
      const sourceValue = readSourceValue(fields, values, rule.sourceFieldId);
      emit(templateId, targetField.id, {
        format: 'B',
        rule,
        operator: cl.operator,
        defaultAction: cl.action,
        matched: evaluateFormatB(rule, sourceValue),
      });
    });
  });
}

/**
 * Compute the set of conditionally-hidden field IDs for one document/template.
 *
 * @param {Array} fields  - field placements for the doc
 * @param {Object} values - current field values, keyed by field id (and groupName for radio)
 * @returns {Set<string>} - set of placement ids that should be hidden
 */
export function computeHiddenFieldIds(fields, values) {
  const hidden = new Set();
  if (!Array.isArray(fields) || fields.length === 0) return hidden;
  fields = fields.filter(Boolean);
  values = values || {};

  const targetRules = new Map();
  collectEntries(fields, values, {
    templateId: null,
    emit: (_targetTemplateId, targetFieldId, entry) => {
      if (!targetRules.has(targetFieldId)) targetRules.set(targetFieldId, []);
      targetRules.get(targetFieldId).push(entry);
    },
  });

  targetRules.forEach((entries, targetId) => {
    if (shouldHideTarget(entries)) hidden.add(targetId);
  });

  return hidden;
}

/**
 * Package-wide evaluation. Identical rule semantics to the single-document
 * path — the only difference is that a Format A rule carrying
 * `targetTemplateId` lands on that document's field instead of its own.
 *
 * @param {Object}   args
 * @param {Array}    args.documents            - [{ document_id, template_id }] in order
 * @param {Object}   args.fieldsByTemplateId   - { [templateId]: placements[] }
 * @param {Object}   args.valuesByDocId        - { [documentId]: values }
 * @returns {Object} { [documentId]: Set<string> }
 */
export function computePackageHiddenFieldIds({ documents = [], fieldsByTemplateId = {}, valuesByDocId = {} }) {
  const docIdByTemplateId = {};
  for (const doc of documents) {
    if (doc?.template_id && doc?.document_id) docIdByTemplateId[doc.template_id] = doc.document_id;
  }

  // targetDocId → targetFieldId → entries
  const byDoc = new Map();
  const emitFor = (ownTemplateId) => (targetTemplateId, targetFieldId, entry) => {
    const targetDocId = docIdByTemplateId[targetTemplateId || ownTemplateId];
    // A rule pointing at a document that is no longer in the package is
    // ignored rather than silently hiding something in the wrong place.
    if (!targetDocId) return;
    if (!byDoc.has(targetDocId)) byDoc.set(targetDocId, new Map());
    const forDoc = byDoc.get(targetDocId);
    if (!forDoc.has(targetFieldId)) forDoc.set(targetFieldId, []);
    forDoc.get(targetFieldId).push(entry);
  };

  for (const doc of documents) {
    const templateId = doc?.template_id;
    const docId = doc?.document_id;
    if (!templateId || !docId) continue;
    const fields = (fieldsByTemplateId[templateId] || []).filter(Boolean);
    if (fields.length === 0) continue;
    collectEntries(fields, valuesByDocId[docId] || {}, {
      templateId,
      emit: emitFor(templateId),
    });
  }

  const out = {};
  for (const doc of documents) {
    const docId = doc?.document_id;
    if (!docId) continue;
    const hidden = new Set();
    const forDoc = byDoc.get(docId);
    if (forDoc) {
      forDoc.forEach((entries, targetFieldId) => {
        if (shouldHideTarget(entries)) hidden.add(targetFieldId);
      });
    }
    out[docId] = hidden;
  }
  return out;
}

/**
 * Builder helper — true when this rule sends its effect to another document.
 */
export function isCrossDocumentRule(rule, ownTemplateId) {
  return Boolean(rule?.targetTemplateId && rule.targetTemplateId !== ownTemplateId);
}
